import React from 'react';
import { useI18nContext } from '@procore/core-react';
import Head from 'next/head';
import { Box } from '@mui/material';
import { useRouter } from 'next/router';
import { type GetServerSidePropsContext } from 'next';
import Header from '../../components/Layout/Header';
import Footer from '../../components/Layout/Footer';
import marketSectors from '../../data/filters/market-sectors';
import routes from '../../helpers/routes';
import StatePageContent from '../../components/Layout/StatePageContent';
import { canonicalUrl } from '../../helpers/canonical-url';
import { FilterUrlKey } from '../../helpers/filters';
import { useStyles } from './[marketSector].styles';
import { usePageView } from '../../components/Analytics/utilities';
import { type SeoListType } from '../../types/seo-list-type';

const getServerSideProps = async (context: GetServerSidePropsContext) => {
  const { marketSector } = context.query;
  const iterableBusinessTypes = Object.values(marketSectors);
  const marketSectorData = iterableBusinessTypes.find(
    (ms) => ms.urlKey === marketSector
  );
  if (marketSectorData) {
    return { props: { marketSector: marketSectorData } };
  }

  return {
    notFound: true,
  };
};

export { getServerSideProps };

export default function MarketSectorPage({
  marketSector,
}: {
  marketSector: SeoListType;
}) {
  const I18n = useI18nContext();
  const classes = useStyles();
  const router = useRouter();
  const canonicalURL = canonicalUrl(router.asPath);
  usePageView(`View Market Sector - ${marketSector.name}`);

  return (
    <>
      <Head>
        <title>
          {I18n.t('pages.market-sectors.market-sector.title', {
            name: marketSector.name,
          })}
        </title>
        <meta
          name="description"
          content={`Search, find and connect with ${marketSector.name} professionals on Procore. Quickly find thousands of construction professionals on Procore ready to build.`}
        />
        <link rel="canonical" key="canonical" href={canonicalURL} />
      </Head>
      <Box className={classes.root}>
        <main>
          <Header />
          <StatePageContent
            hero={{
              title: I18n.t('pages.market-sectors.market-sector.hero.title', {
                name: marketSector.name,
              }),
              subTitle: I18n.t(
                'pages.market-sectors.market-sector.hero.sub-title',
                {
                  name: marketSector.name,
                }
              ),
            }}
            item={marketSector}
            itemType={FilterUrlKey.constructionSector}
            getStateLink={(sector, country, state) =>
              routes.marketSectors(sector.urlKey, country, state)
            }
          />
        </main>
        <Footer />
      </Box>
    </>
  );
}
